/* ---------------------------------------------------------------------------
 * Event template photo slot
 * ---------------------------------------------------------------------------
 * The `{image}` placeholder an Event Template holds for the event's own photo,
 * and the box its author sizes that photo into.
 *
 * A template is authored once and drawn for every event that picks it, so the
 * photo's footprint has to be decided by the template rather than by whichever
 * photo an event happens to carry. That is what the slot is: a box the author
 * drags to the size they want, saved as
 *
 *     <span data-event-image="true" data-width="50" data-ratio="1.5"
 *           style="... width: 50%; aspect-ratio: 1.5">{image}</span>
 *
 * with the token still inside it. Nothing about the substitution in
 * `renderEventContent` changes — it goes on replacing `{image}` with the event's
 * <img>, which now simply lands inside the sized box and is drawn
 * `object-fit: contain`: fitted, never cropped, never stretched out of shape. So
 * a portrait photo and a landscape one occupy the identical footprint, and the
 * lines under the photo never move between events.
 *
 * The width is a PERCENTAGE, and the height an aspect ratio of that width. That
 * is what makes the box travel. A percentage resolves against the artboard the
 * template is drawn into, so the photo holds the same proportion of the board on
 * the dashboard's small preview, on a 1080p screen and on a 4K one — the same
 * proportion it held on the editor's canvas, which resolves its percentage the
 * same way against the editor's own width.
 *
 * Both surfaces build their geometry from the functions here, so the placeholder
 * an author drags (Event_Image_Placeholder.vue, mounted by the `eventImage` node
 * in Rich_Text_Editor.vue) and the markup that is saved cannot drift apart.
 */

/** The token itself. Substituted by `renderEventContent` in Display.vue. */
export const EVENT_IMAGE_TOKEN = '{image}';

/** Marks a slot. The editor parses it back, and Display.vue styles it. */
export const EVENT_IMAGE_SLOT_ATTR = 'data-event-image';

/** Carries the authored geometry through a save / load round trip. */
export const EVENT_IMAGE_WIDTH_ATTR = 'data-width';
export const EVENT_IMAGE_RATIO_ATTR = 'data-ratio';

/** Styling hook for the rendered box and the photo fitted inside it. */
export const EVENT_IMAGE_SLOT_CLASS = 'event-template-photo-slot';

/* Width, as a percentage of the artboard column the template is drawn into.
 * The floor is a box that is still visibly a photo rather than a speck; the
 * ceiling is the full column, since a percentage over 100 would only overflow. */
export const EVENT_IMAGE_MIN_WIDTH = 5;
export const EVENT_IMAGE_MAX_WIDTH = 100;
export const EVENT_IMAGE_DEFAULT_WIDTH = 50;
/** One press of the toolbar's - / + . */
export const EVENT_IMAGE_WIDTH_STEP = 5;

/* Height, as `width / ratio`. Bounded so a stray drag cannot leave a slot so
 * flat or so tall that the author can no longer grab a handle to fix it. */
export const EVENT_IMAGE_MIN_RATIO = 0.25;
export const EVENT_IMAGE_MAX_RATIO = 4;
/** 3:2 — an ordinary photo, and what a fresh slot starts at. */
export const EVENT_IMAGE_DEFAULT_RATIO = 1.5;

const round = (value: number, places: number): number => {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
};

const clamp = (value: unknown, min: number, max: number, fallback: number, places: number): number => {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  if (!Number.isFinite(parsed)) return fallback;
  return round(Math.min(Math.max(parsed, min), max), places);
};

/**
 * A width the box can actually be drawn at, from anything at all — a saved
 * attribute string, a half-finished drag, a hand-edited value.
 */
export const clampEventImageWidth = (value: unknown): number =>
  clamp(value, EVENT_IMAGE_MIN_WIDTH, EVENT_IMAGE_MAX_WIDTH, EVENT_IMAGE_DEFAULT_WIDTH, 2);

/** The same for the box's width-to-height ratio. */
export const clampEventImageRatio = (value: unknown): number =>
  clamp(value, EVENT_IMAGE_MIN_RATIO, EVENT_IMAGE_MAX_RATIO, EVENT_IMAGE_DEFAULT_RATIO, 4);

/**
 * The box's geometry.
 *
 * Written inline rather than into a stylesheet because the same declarations
 * have to hold on three surfaces that share no CSS: the editor's canvas, the
 * live display and the dashboard's embedded preview. Only the geometry lives
 * here — how the photo sits inside the box, and what an empty box does, are the
 * renderer's business.
 *
 * The editor's placeholder binds this object and the saved markup serialises it,
 * so the box an author drags is laid out by the very same declarations that
 * reach the screen.
 */
export const eventImageSlotStyleObject = (
  width: unknown,
  ratio: unknown
): Record<string, string> => ({
  display: 'inline-block',
  'vertical-align': 'middle',
  width: `${clampEventImageWidth(width)}%`,
  'max-width': '100%',
  'aspect-ratio': String(clampEventImageRatio(ratio)),
});

/** The same declarations as a `style` attribute. */
export const eventImageSlotStyle = (width: unknown, ratio: unknown): string => {
  const declarations = eventImageSlotStyleObject(width, ratio);
  return Object.keys(declarations)
    .map((property) => `${property}: ${declarations[property]};`)
    .join(' ');
};

/** The box's height, as the same kind of percentage its width is given in. */
export const eventImageHeightPercent = (width: unknown, ratio: unknown): number =>
  round(clampEventImageWidth(width) / clampEventImageRatio(ratio), 2);

/** The slot's attributes, for a DOM spec or an element. */
export const eventImageSlotAttributes = (
  width: unknown,
  ratio: unknown
): Record<string, string> => {
  const w = clampEventImageWidth(width);
  const r = clampEventImageRatio(ratio);
  return {
    class: EVENT_IMAGE_SLOT_CLASS,
    [EVENT_IMAGE_SLOT_ATTR]: 'true',
    [EVENT_IMAGE_WIDTH_ATTR]: String(w),
    [EVENT_IMAGE_RATIO_ATTR]: String(r),
    style: eventImageSlotStyle(w, r),
  };
};

/** One slot element, token and all, in the given document. */
export const createEventImageSlotElement = (
  doc: Document,
  width: unknown = EVENT_IMAGE_DEFAULT_WIDTH,
  ratio: unknown = EVENT_IMAGE_DEFAULT_RATIO
): HTMLElement => {
  const element = doc.createElement('span');
  const attributes = eventImageSlotAttributes(width, ratio);
  Object.keys(attributes).forEach((name) => {
    // The style goes through the CSSOM rather than being set as a raw
    // attribute, because that is the road ProseMirror's serialiser takes when
    // the editor writes the same slot back out (`dom.style.cssText = …`), and a
    // browser rewrites some of what it is given on the way through — an
    // `aspect-ratio: 1.5` comes back out as `1.5 / 1`. Going the same way here
    // means a slot built by this function and one saved by the editor are the
    // same bytes, rather than two spellings of the same box.
    if (name === 'style') {
      element.style.cssText = attributes[name];
      return;
    }
    element.setAttribute(name, attributes[name]);
  });
  element.appendChild(doc.createTextNode(EVENT_IMAGE_TOKEN));
  return element;
};

/**
 * Give a bare `{image}` a box.
 *
 * Every template written before the slot existed holds the token as plain text,
 * and so does one where somebody typed the token by hand instead of reaching for
 * the Variable menu. Both still render — Display.vue substitutes a loose token
 * exactly as it always did — but neither can be resized, and neither would show
 * the author a placeholder when they opened the template to edit it.
 *
 * So the Edit modal runs its saved body through here on the way into the editor:
 * each loose token becomes a slot at the default size, which is the size the
 * token was already effectively drawn at. Tokens that are already inside a slot
 * are left alone, which is what keeps this safe to run over content that has
 * been through it before.
 *
 * The string is handed straight back when there is nothing to do, so a template
 * without a photo in it is never rewritten by a round trip through the parser.
 */
export const upgradeEventImageTokens = (html: string): string => {
  if (!html || !html.includes(EVENT_IMAGE_TOKEN)) return html;

  const parsed = new DOMParser().parseFromString(
    `<div id="event-image-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('event-image-root');
  if (!root) return html;

  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  // Collected first, replaced after: swapping a text node out from under a live
  // TreeWalker is how a walk quietly skips its neighbours.
  const loose: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (!text.data.includes(EVENT_IMAGE_TOKEN)) continue;
    if (text.parentElement?.closest(`[${EVENT_IMAGE_SLOT_ATTR}]`)) continue;
    loose.push(text);
  }

  if (!loose.length) return html;

  loose.forEach((text) => {
    const pieces = text.data.split(EVENT_IMAGE_TOKEN);
    const fragment = doc.createDocumentFragment();
    pieces.forEach((piece, index) => {
      if (index > 0) fragment.appendChild(createEventImageSlotElement(doc));
      if (piece) fragment.appendChild(doc.createTextNode(piece));
    });
    text.parentNode?.replaceChild(fragment, text);
  });

  return root.innerHTML;
};
